/*
 * Shared chart chrome: recessive axes, the legend, and one tooltip used by
 * every chart so the hover layer looks identical everywhere.
 */

import type { TooltipContentProps } from 'recharts';

/** Any object is acceptable chart input; the series keys do the addressing. */
export type ChartDatum = object;

export const AXIS_STYLE = {
  fontSize: 11,
  fill: 'var(--text-muted)',
} as const;

export const axisProps = {
  tick: AXIS_STYLE,
  tickLine: false,
  axisLine: false,
  stroke: 'var(--axis)',
} as const;

export const gridProps = {
  stroke: 'var(--grid)',
  strokeDasharray: '0',
  vertical: false,
} as const;

export interface SeriesDef {
  key: string;
  label: string;
  color: string;
}

export function Legend({ series }: { series: SeriesDef[] }) {
  // A single series needs no legend — the card title already names it.
  if (series.length < 2) return null;
  return (
    <div className="legend">
      {series.map((s) => (
        <span className="legend__item" key={s.key}>
          <span className="legend__swatch" style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

type Formatter = (value: number, key: string) => string;

export function makeTooltip(labelFn: (label: string) => string, valueFn: Formatter) {
  // Recharts types the content callback against its own default generics, so
  // match those and narrow the value at the point of use.
  return function Tip({ active, payload, label }: TooltipContentProps) {
    if (!active || !payload?.length) return null;
    return (
      <div className="tip">
        <div className="tip__title">{labelFn(String(label))}</div>
        {payload.map((entry) => (
          <div className="tip__row" key={String(entry.dataKey)}>
            <span className="dot" style={{ background: entry.color ?? 'var(--text-muted)' }} />
            {entry.name}
            <b>{valueFn(Number(entry.value ?? 0), String(entry.dataKey))}</b>
          </div>
        ))}
      </div>
    );
  };
}

/** Hover wash behind the crosshair — bigger hit feedback than the mark itself. */
export const CURSOR = { fill: 'var(--accent-wash)' } as const;
export const LINE_CURSOR = { stroke: 'var(--axis)', strokeWidth: 1 } as const;
